<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import {
  baseInput,
  baseSelect,
  baseConfirm,
  svgIcon,
} from "@/common/components";

import type {
  MethodFieldType,
  MethodFieldPayloadType,
} from "@/common/types/method/method-field";

const { t } = useI18n();

defineProps<{
  data: MethodFieldType[] | null;
  answer?: boolean;
}>();

const emit = defineEmits(["cancel", "add", "edit", "del"]);

const payloadDefault = {
  name: "",
  type: "text",
  required: false,
};

const payload = ref<MethodFieldPayloadType>({ ...payloadDefault });
const selected = ref<MethodFieldType | null>(null);
const showModalDel = ref(false);

const type = ref<string | null>(null);

const typeOptions = computed(() => [
  {
    option: t("common.labels.number"),
    value: "NUMBER",
  },
  {
    option: t("question.labels.range"),
    value: "RANGE",
  },
  {
    option: t("question.types.input"),
    value: "TEXT",
  },
  {
    option: t("question.types.textarea"),
    value: "TEXTAREA",
  },
]);

const delMessage = computed(() => {
  const message = t("method.texts.del_field_confirm", {
    concat: selected.value?.name,
  });
  const problem = t("common.texts.item_message");
  const undo = t("common.texts.undo_message");

  return `${message}<br>${problem}<br>${undo}`;
});

function cancel() {
  type.value = null;
  selected.value = null;
  showModalDel.value = false;
  payload.value = { ...payloadDefault };
}

function add() {
  type.value = "add";
  payload.value = { ...payloadDefault };
}

function addConfirm() {
  emit("add", payload.value);
  cancel();
}

function edit(field: MethodFieldType) {
  type.value = "edit";
  selected.value = field;
  payload.value = field;
}

function editConfirm() {
  emit("edit", payload.value);
  cancel();
}

function del(field: MethodFieldType) {
  selected.value = field;
  showModalDel.value = true;
}

function delConfirm() {
  emit("del", selected.value);
  cancel();
}
</script>

<template>
  <div class="field-content">
    <div class="header">
      <template v-if="type && payload">
        <div class="form">
          <div class="w-50">
            <base-select
              v-model="payload.type"
              :value="payload.type"
              :options="typeOptions"
              :is-empty="false"
            >
              <template #label>
                {{ t("common.labels.type") }}
              </template>
            </base-select>
          </div>

          <base-input
            v-model="payload.name"
            :value="payload?.name"
            :placeholder="t('question.placeholders.name')"
          >
            <template #label>
              {{ t("common.labels.name") }}
            </template>
          </base-input>
        </div>
      </template>

      <div class="actions">
        <template v-if="type === 'add'">
          <button
            class="btn btn-md btn-primary"
            type="submit"
            @click="addConfirm()"
          >
            <svg-icon icon="save" size="1.5rem" />
            {{ t("common.labels.save") }}
          </button>

          <button type="button" class="btn btn-md cancel" @click="cancel()">
            {{ t("common.labels.cancel") }}
          </button>
        </template>

        <template v-else-if="type === 'edit'">
          <button
            class="btn btn-md btn-primary"
            type="submit"
            @click="editConfirm()"
          >
            <svg-icon icon="edit" size="1.5rem" />
            {{ t("common.labels.edit") }}
          </button>

          <button type="button" class="btn btn-md cancel" @click="cancel()">
            {{ t("common.labels.cancel") }}
          </button>
        </template>

        <template v-else>
          <button type="button" class="btn btn-md btn-primary" @click="add()">
            <svg-icon icon="add" size="1rem" />
            {{ t("common.labels.add") }}
          </button>
        </template>
      </div>
    </div>

    <div class="content">
      <div class="item">
        <div>{{ t("common.labels.code") }}</div>
        <div class="actions">
          <button type="button" disabled>
            <svg-icon icon="edit" size="1rem" />
          </button>
          <button type="button" disabled>
            <svg-icon icon="trash" size="1rem" />
          </button>
        </div>
      </div>

      <div class="item">
        <div>{{ t("common.labels.name") }}</div>
        <div class="actions">
          <button type="button" disabled>
            <svg-icon icon="edit" size="1rem" />
          </button>
          <button type="button" disabled>
            <svg-icon icon="trash" size="1rem" />
          </button>
        </div>
      </div>

      <template v-for="(field, index) in data" :key="index">
        <div
          class="item"
          :class="{
            active: selected?.field_id === field.field_id,
          }"
        >
          <div>{{ field.name }}</div>
          <div class="actions">
            <button type="button" @click="edit(field)">
              <svg-icon icon="edit" size="1rem" />
            </button>
            <button type="button" @click="del(field)">
              <svg-icon icon="trash" size="1rem" />
            </button>
          </div>
        </div>
      </template>
    </div>

    <base-confirm
      :show="showModalDel"
      :title="t('method.texts.del_field')"
      :message="delMessage"
      @cancel="cancel()"
      @confirm="delConfirm()"
    />
  </div>
</template>

<style lang="scss" scoped>
.field-content {
  display: flex;
  flex-direction: column;

  .header {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    background: var(--white-off);
    padding: 0.75rem;
    border-radius: 0.5rem;

    .form {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 0.75rem;
    }

    .actions {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
    }
  }

  .content {
    display: flex;
    flex-wrap: wrap;
    padding-block: 2rem;
    gap: 1rem;

    .item {
      user-select: none;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 1rem;
      padding-block: 0.5rem;
      padding-inline: 0.75rem;
      border: 1px solid var(--border);
      border-radius: 0.5rem;

      &.active {
        border: 2px solid transparent;
        background: var(--primary);
        color: var(--primary-color);
      }

      .actions {
        user-select: none;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
      }

      button {
        cursor: pointer;
        user-select: none;
        font-size: 0.75rem;
        line-height: 0.75rem;
        background: #fff;
        outline: none;
        border: 1px solid var(--border);
        border-radius: 0.25rem;
        padding-block: 5px;
        padding-inline: 7px;

        &:hover {
          background: var(--gray);
        }

        &:disabled {
          cursor: default;
          background: white;
          border-color: #e5e5e5;
        }
      }
    }
  }
}
</style>
